import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Bot, ChevronLeft, ChevronRight, RefreshCw, AlertTriangle } from 'lucide-react'
import { aiUsageApi } from '../../lib/api'
import { formatDatetime } from '../../lib/utils'
import { tx } from '../../lib/i18n'

const baht = (satang: number) => `฿${(satang / 100).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const num = (n: number) => n.toLocaleString('th-TH')
const bkkMonth = (offset = 0) => {
  const d = new Date(Date.now() + 7 * 3600_000)
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() + offset)
  return d.toISOString().slice(0, 7)
}
const shiftMonth = (m: string, by: number) => {
  const d = new Date(Date.parse(m + '-01T00:00:00Z'))
  d.setUTCMonth(d.getUTCMonth() + by)
  return d.toISOString().slice(0, 7)
}
const monthLabel = (m: string) => new Date(Date.parse(m + '-01T00:00:00Z')).toLocaleDateString('th-TH', { month: 'long', year: 'numeric', timeZone: 'UTC' })

const AiUsagePage: React.FC = () => {
  const [month, setMonth] = useState(bkkMonth())
  const [openShop, setOpenShop] = useState<string | null>(null)

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['ai-usage', month],
    queryFn: () => aiUsageApi.summary(month).then((r) => r.data.data),
  })
  const detail = useQuery({
    queryKey: ['ai-usage', month, openShop],
    queryFn: () => aiUsageApi.shop(openShop as string, month).then((r) => r.data.data),
    enabled: !!openShop,
  })

  const total = data?.total
  const shops = data?.shops ?? []
  const days = data?.days ?? []
  const maxDay = Math.max(1, ...days.map((d) => d.costSatang))

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Bot className="w-5 h-5 text-indigo-600" />{tx('AI Usage')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{tx('What each shop\'s AI analysis costs us (Claude API), per month')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(shiftMonth(month, -1))} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-medium min-w-[150px] text-center">{monthLabel(month)}</span>
          <button onClick={() => setMonth(shiftMonth(month, 1))} disabled={month >= bkkMonth()} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={() => refetch()} disabled={isFetching} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />{tx('Refresh')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: tx('Total cost'), value: total ? baht(total.costSatang) : '—', sub: total ? `≈ $${total.costUsd.toFixed(2)}` : '' },
          { label: tx('Shops using AI'), value: total ? num(total.shops) : '—', sub: '' },
          { label: tx('Reports'), value: total ? num(total.reports) : '—', sub: tx('nightly + manual') },
          { label: tx('Questions'), value: total ? num(total.asks) : '—', sub: tx('Ask AI') },
          { label: tx('Tokens'), value: total ? num(total.inputTokens + total.outputTokens) : '—', sub: total ? `${tx('in')} ${num(total.inputTokens)} · ${tx('out')} ${num(total.outputTokens)}` : '' },
        ].map((c) => (
          <div key={c.label} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-500">{c.label}</div>
            <div className="text-xl font-bold text-gray-900 mt-1">{c.value}</div>
            {c.sub && <div className="text-xs text-gray-400 mt-0.5">{c.sub}</div>}
          </div>
        ))}
      </div>

      {total && total.errors > 0 && (
        <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4" /> {tx('Failed AI calls this month')}: {num(total.errors)}
        </div>
      )}

      {days.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-sm font-medium text-gray-700 mb-3">{tx('Daily cost')}</div>
          <div className="flex items-end gap-1 h-28">
            {days.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center justify-end h-full" title={`${d.day} · ${d.calls} ${tx('calls')} · ${baht(d.costSatang)}`}>
                <div className="w-full bg-indigo-500/80 rounded-t" style={{ height: `${Math.max(3, (d.costSatang / maxDay) * 100)}%` }} />
                <div className="text-[10px] text-gray-400 mt-1">{d.day.slice(8)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">{tx('Shop')}</th>
              <th className="text-right px-4 py-2">{tx('Reports')}</th>
              <th className="text-right px-4 py-2">{tx('Questions')}</th>
              <th className="text-right px-4 py-2">{tx('Tokens')}</th>
              <th className="text-right px-4 py-2">{tx('Cost')}</th>
              <th className="text-left px-4 py-2">{tx('Last call')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">{tx('Loading...')}</td></tr>}
            {!isLoading && shops.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">{tx('No AI usage this month')}</td></tr>}
            {shops.map((s) => (
              <React.Fragment key={s.shopId}>
                <tr className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setOpenShop(openShop === s.shopId ? null : s.shopId)}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900">{s.shopName}</div>
                    <Link to={`/shops/${s.shopId}`} onClick={(e) => e.stopPropagation()} className="text-xs text-indigo-600 hover:underline">{tx('Open shop')}</Link>
                    {s.errors > 0 && <span className="ml-2 text-xs text-amber-600">{s.errors} {tx('failed')}</span>}
                  </td>
                  <td className="px-4 py-2 text-right">{num(s.reports)}</td>
                  <td className="px-4 py-2 text-right">{num(s.asks)}</td>
                  <td className="px-4 py-2 text-right">{num(s.inputTokens + s.outputTokens)}</td>
                  <td className="px-4 py-2 text-right font-medium">{baht(s.costSatang)}</td>
                  <td className="px-4 py-2 text-gray-500">{formatDatetime(s.lastAt)}</td>
                </tr>
                {openShop === s.shopId && (
                  <tr className="bg-gray-50/70">
                    <td colSpan={6} className="px-4 py-3">
                      {detail.isLoading ? (
                        <div className="text-xs text-gray-400">{tx('Loading...')}</div>
                      ) : (
                        <table className="w-full text-xs">
                          <thead className="text-gray-500">
                            <tr><th className="text-left py-1">{tx('When')}</th><th className="text-left py-1">{tx('Type')}</th><th className="text-left py-1">{tx('Model')}</th><th className="text-right py-1">in / out</th><th className="text-right py-1">{tx('Cost')}</th><th className="text-left py-1 pl-3">{tx('Note')}</th></tr>
                          </thead>
                          <tbody>
                            {(detail.data?.calls ?? []).map((c) => (
                              <tr key={c.id} className="border-t border-gray-200/70">
                                <td className="py-1 whitespace-nowrap">{formatDatetime(c.createdAt)}</td>
                                <td className="py-1">{c.kind === 'daily' ? tx('Nightly report') : c.kind === 'manual' ? tx('Manual report') : tx('Question')}</td>
                                <td className="py-1 text-gray-500">{c.model}</td>
                                <td className="py-1 text-right text-gray-500">{num(c.inputTokens)} / {num(c.outputTokens)}</td>
                                <td className="py-1 text-right">{baht(c.costSatang)}</td>
                                <td className="py-1 pl-3 text-gray-500 max-w-[420px] truncate">{c.status === 'error' ? <span className="text-red-600">{c.error}</span> : c.question || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AiUsagePage
